import { useState } from "react";
import { SM, hydrateSingleLeg, betDisplayStatus, calcPayout, fmtOdds, hasLineValue, isNegativeLine, effectiveParlayOdds, settledWonPayout, isUntrackedLeg, isDisplayDnpLeg, gradingLeague, readsPlayerStat, isNoBinaryLeg } from "../lib/betting";
import { sportsbookForDisplay } from "../lib/sportsbooks";
import { getMarketLabel, getMarketShort, isBinaryPlayerProp, marketKey } from "../lib/markets";
import { teamLegLineText } from "../lib/leg-line-text";
import { singleMarketSource } from "../lib/bet-root-display";
import { formatPropLabel, safeOverUnder, fmtMoney } from "../lib/format";
import { supabase } from "../lib/supabase";
import { apiFetch } from "../lib/api";
import { randomUUID } from "../lib/platform";
import { JT } from "../components/ui/jumbotronTokens";
import { HELPLINE_TEL, HELPLINE_LABEL } from "../components/ui/HelplineCard";
import { SyncSkeleton, SyncErrorCard } from "../components/ui/SyncState";

// #1353 PR 3b (r5b.4 I1 / r5b.5 J1): Results grades and pays with the server-written DNP flag trusted.
const TRUST_DNP = { trustDnpFlag: true };

// #789 co-requisite (spec 2026-07-03-resultspage-unresolvable-display): on a settled
// card, ONLY `lost` is a stake loss and ONLY `won`/`lost`/`push` are resolved money.
// Everything else — `unresolvable` (MISSING DATA), `void` (VOID), or a non-terminal
// `parlayStatus` fallback (`pending`/`winning`/`losing`) for a settledAt bet with no
// parent `finalResult` — is a non-loss: it must render neutral at $0 (never -$stake
// in red) and be excluded from the ROI/staked denominators. parlayStatus returns
// `pending` for a multi held by an `unresolvable` leg that can still change, and
// `unresolvable` only once that leg is statically ungradeable on a FINAL game (#1138), so
// recognizing only unresolvable/void would still render a held missing-data parlay as a
// loss (Codex P1, PR #798). `net` (won payouts − lost stakes) already excludes non-won/lost — unchanged.
// Helpers are exported pure functions so the money-display contract is unit-testable
// without a full component render.
export function isResolvedStatus(s) {
  return s === "won" || s === "lost" || s === "push";
}

// Result cell for a settled bet. `display` fills the collapsed money slot;
// `label`/`value`/`color` fill the expanded Stake/Odds/result breakdown. Only `lost`
// renders as a red -$stake loss; every non-resolved status is neutral $0. Only
// `unresolvable` (amber MISSING DATA) and `void` (grey VOID) carry an informative SM
// color; every other non-resolved status — `winning` (green), `losing` (red), `close`
// (orange), `pending`, or any unknown/prototype-key value — must NOT render in a
// win/loss/in-play color on a settled card, so it resolves to neutral grey SM.pending.
// The (s===...) whitelist also avoids a bogus SM[prototypeKey] lookup. Never red for a
// non-loss. (Pre-Codex: Fail-Closed + Security P2, PR #798.)
export function resultBadge(s, stake, payout) {
  // #878: a won parlay/SGP whose payout is INDETERMINATE (pushed leg, book-adjusted)
  // shows "Won" with NO dollar amount — never the over-stated pre-void figure (INV-1/INV-7).
  if (s === "won")  return payout == null
    ? { display: "Won", label: "Won", value: "—", color: JT.status.won.color }
    : { display: fmtMoney(payout), label: "Won", value: fmtMoney(payout), color: JT.status.won.color };
  // Money strings come from fmtMoney (UI critique item A): sign before the `$`, always 2 dp,
  // zero unsigned. `lost` is the only status that passes a negative (#789 contract).
  if (s === "lost") return { display: fmtMoney(-stake),  label: "Lost", value: fmtMoney(-stake),  color: JT.status.lost.color };
  if (s === "push") return { display: fmtMoney(0),       label: "Push", value: fmtMoney(0),       color: JT.status.neutral.color };
  // #1655 B3b: `not_graded` keeps its own neutral NOT GRADED badge (defensive -- such a bet is
  // unsettled, so it rarely reaches History).
  const sm = (s === "unresolvable" || s === "void" || s === "not_graded") ? SM[s] : SM.pending;
  return { display: fmtMoney(0), label: sm.label, value: fmtMoney(0), color: sm.color };
}

// Total stake over resolved money only (won/lost/push) — the ROI denominator.
// statusFn maps a bet to its computed status (getS). #878 F4: isEligible lets the
// caller drop a won-but-INDETERMINATE bet (its payout is excluded from `net`) so the
// ROI numerator and denominator describe the same confident population — never a
// win counted in the denominator whose payout was excluded from the numerator.
export function resolvedStaked(bets, statusFn, isEligible = () => true) {
  return bets.reduce((sum, b) => (isResolvedStatus(statusFn(b)) && isEligible(b)) ? sum + Number(b.stake || 0) : sum, 0);
}

export default function ResultsPage({bets,games,refetchBetaStatus,syncLoading=false,syncError=false,onRetrySync,onRequestRemove}) {
  const [expanded, setExpanded] = useState({});
  const [insights, setInsights] = useState({}); // betId → {status: "loading"|"done"|"error", text}
  // UI critique PR 2: text roles come from JT (Readable Floor -- tertiary or lighter, >= 11px);
  // pinned by ui-text-role-floor.test.js + ui-label-floor.test.js. Settled-card win/loss
  // carriers (washes, stripes, glows) are untouched here -- PR 3 Signal Scarcity owns them.
  const eyebrow = {...JT.type.label, color:JT.text.tertiary};
  const tile = {background:JT.bg.well, border:`1px solid ${JT.border.default}`};
  const pnl = (n) => n>=0 ? JT.status.won.color : JT.status.lost.color;


  const getInsight = async (bet) => {
    // #915: ignore a re-tap while a request for this bet is in flight — a concurrent
    // call would mint a second key and double-charge the reserve for one user intent.
    if (insights[bet.id]?.status === "loading") return;
    // #915: one idempotency key per logical insight action for this bet. Reuse the prior
    // key on a retry (previous attempt errored) so the reserve replays and doesn't double-
    // charge; mint a fresh one otherwise. Cleared on success so the next attempt mints fresh.
    const prevInsight = insights[bet.id];
    const idemKey = (prevInsight && prevInsight.status === "error" && prevInsight.idemKey)
      ? prevInsight.idemKey
      : randomUUID();
    setInsights(prev=>({...prev,[bet.id]:{status:"loading",text:null,idemKey}}));
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        setInsights(prev=>({...prev,[bet.id]:{status:"error",text:null,idemKey}}));
        return;
      }
      const res = await apiFetch("/api/slip-insight",{
        method:"POST",
        headers:{"Content-Type":"application/json","Authorization":`Bearer ${session.access_token}`,"Idempotency-Key":idemKey},
        body:JSON.stringify({slip:bet, history:settled.filter(b=>b.id!==bet.id)}),
      });
      if(!res.ok) {
        if (res.status === 403 && refetchBetaStatus) refetchBetaStatus();
        throw new Error("Failed");
      }
      const data = await res.json();
      setInsights(prev=>({...prev,[bet.id]:{status:"done",text:data.insight}}));
    } catch {
      setInsights(prev=>({...prev,[bet.id]:{status:"error",text:null,idemKey}}));
    }
  };
  const settled = bets.filter(b=>b.settledAt).sort((a,b)=>b.settledAt-a.settledAt);
  // #1037 round 3 P1: no `b.league ?? 'nhl'` default -- see betDisplayStatus.
  const getS = b => betDisplayStatus(b,games,TRUST_DNP);
  const won  = settled.filter(b=>getS(b)==="won");
  const lost = settled.filter(b=>getS(b)==="lost");
  const push = settled.filter(b=>getS(b)==="push");
  // #878: void-aware — reduced payout for a won parlay/SGP with a pushed leg;
  // indeterminate ones are excluded from the sum (INV-6), never zeroed/estimated.
  const net  = won.reduce((s,b)=>{const p=settledWonPayout(b,games,TRUST_DNP);return p==null?s:s+p;},0)
             - lost.reduce((s,b)=>s+(Number(b.stake)||0),0);
  // #878 F4: exclude won-but-indeterminate bets from the ROI denominator too (their
  // payout is already out of `net`), so ROI describes the same confident population.
  const totalStaked = resolvedStaked(settled, getS, b => !(getS(b)==="won" && settledWonPayout(b,games,TRUST_DNP)===null));
  const roi = totalStaked>0 ? (net/totalStaked)*100 : 0;

  // Best sportsbook by net P&L
  // #1116 Ultron F7: Object.create(null) -- a stored sportsbook of '__proto__' must
  // never write onto Object.prototype (sbMap[id]={...} would otherwise pollute it).
  const sbMap = Object.create(null);
  settled.forEach(b=>{
    const id = b.sportsbook||"unknown"; // #1199: a bet with no book is its own bucket, never FanDuel's
    if(!sbMap[id]) sbMap[id]={won:0,lost:0,push:0,net:0,staked:0};
    const s=getS(b);
    const wonPayout = s==="won" ? settledWonPayout(b,games,TRUST_DNP) : null;
    const indeterminateWon = s==="won" && wonPayout==null; // #878: won but payout not derivable
    if(s==="won"){sbMap[id].won++;if(wonPayout!=null)sbMap[id].net+=wonPayout;} // #878: exclude indeterminate (INV-6)
    else if(s==="lost"){sbMap[id].lost++;sbMap[id].net-=Number(b.stake||0);}
    else if(s==="push"){sbMap[id].push++;}
    if(isResolvedStatus(s) && !indeterminateWon) sbMap[id].staked+=Number(b.stake||0); // #878 F4: out of ROI denominator
  });
  const bestSb = Object.entries(sbMap).sort((a,b)=>b[1].net-a[1].net)[0];
  const bestSbInfo = bestSb ? sportsbookForDisplay(bestSb[0]) : null;

  // P&L by prop type
  // #1116: same guard as sbMap -- the key comes from a stored market string.
  // #1681: a single's market lives on its leg (rowToBet sets no root `type`), so read it
  // through singleMarketSource. A row is one league's label: a legacy id and its canonical id
  // (moneyline / nhl:moneyline) share it, but nhl:hits and mlb:hits are different stats under
  // one label, so the league is named when two leagues would show the same label (pre-Codex F1).
  // A single with no market (untracked) is its own OTHER row, keyed apart from every market.
  const propMap = Object.create(null);
  settled.forEach(b=>{
    let key, label, league = "", market = marketKey(singleMarketSource(b));
    if(typeof market!=="string") market = ""; // a stored value that is not text names no market
    if(b.slipType==="sgp"||b.slipType==="parlay"){ key=b.slipType; label=b.slipType==="sgp"?"SGP":"Parlay"; market=""; }
    else if(!market){ key="other"; label="OTHER"; }
    else {
      label = getMarketLabel(market)||market.toUpperCase();
      league = (market.includes(":") ? market.split(":")[0] : (b.league||"")).toLowerCase();
      key = `single:${league}:${label}`;
    }
    if(!propMap[key]) propMap[key]={label,league,market,won:0,lost:0,push:0,net:0};
    const s=getS(b);
    if(s==="won"){propMap[key].won++;const p=settledWonPayout(b,games,TRUST_DNP);if(p!=null)propMap[key].net+=p;} // #878: exclude indeterminate (INV-6)
    else if(s==="lost"){propMap[key].lost++;propMap[key].net-=Number(b.stake||0);}
    else if(s==="push"){propMap[key].push++;}
  });
  const propRows = Object.entries(propMap).sort((a,b)=>b[1].net-a[1].net);
  const labelLeagues = Object.create(null); // label -> how many league rows carry it
  propRows.forEach(([,d])=>{ if(d.league) labelLeagues[d.label]=(labelLeagues[d.label]||0)+1; });
  propRows.forEach(([,d])=>{ if(d.league && labelLeagues[d.label]>1) d.label=`${d.league.toUpperCase()} ${d.label}`; });
  // Nova P3-1: a market row whose label still matches another row (an unknown id that reads
  // OTHER / SGP, a null-league bet beside a league one) is named by its market id as well.
  const labelRows = Object.create(null);
  propRows.forEach(([,d])=>{ labelRows[d.label]=(labelRows[d.label]||0)+1; });
  propRows.forEach(([,d])=>{ if(d.market && labelRows[d.label]>1) d.label=`${d.label} (${d.market})`; });

  return (
    <div style={{padding:"20px 16px",animation:"fadeUp .3s ease"}}>
      {syncLoading?(
        <SyncSkeleton count={4} height={96}/>
      ):syncError?(
        <SyncErrorCard onRetry={onRetrySync}/>
      ):settled.length===0?(
        <div style={{textAlign:"center",padding:"60px 24px",animation:"fadeUp .4s ease"}}>
          <div style={{fontSize:64,marginBottom:16,filter:"drop-shadow(0 0 20px rgba(148,163,184,.2))"}}>📊</div>
          <div style={{fontSize:20,fontWeight:900,color:JT.text.primary,marginBottom:8,letterSpacing:"-0.3px"}}>No results yet</div>
          <div style={{fontSize:13,color:JT.text.tertiary,marginBottom:28,lineHeight:1.6}}>Your settled bets and performance stats will appear here once your first slip is graded.</div>
          <div style={{...tile,borderRadius:14,padding:"16px",textAlign:"left"}}>
            <div style={{...eyebrow,letterSpacing:"1px",marginBottom:10}}>What you'll see here</div>
            {[["📈","W/L record and win rate"],["💰","Total profit and ROI"],["🏆","Best sportsbook breakdown"],["📋","Full bet history"]].map(([icon,label])=>(
              <div key={label} style={{display:"flex",alignItems:"center",gap:10,marginBottom:8}}>
                <span style={{fontSize:16}}>{icon}</span>
                <span style={{fontSize:12,color:JT.text.tertiary}}>{label}</span>
              </div>
            ))}
          </div>
        </div>
      ):(
        <>
          {/* P&L Banner */}
          <div style={{background:net>=0?"#001a0a":"#1c0101",border:`1px solid ${net>=0?"#4ade8033":"#f8717133"}`,borderRadius:14,padding:"16px 18px",marginBottom:16,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <div>
              <div style={{...eyebrow,letterSpacing:"1px",marginBottom:4}}>Total Profit</div>
              <div style={{fontSize:28,fontWeight:900,color:pnl(net)}}>{fmtMoney(net)}</div>
            </div>
            <div style={{textAlign:"right"}}>
              <div style={{fontSize:22,fontWeight:900,color:JT.text.primary}}>{won.length}–{lost.length}{push.length>0?`–${push.length}`:""}</div>
              <div style={{fontSize:11,color:JT.text.tertiary,marginTop:2}}>{push.length>0?"W–L–P":"W–L"}</div>
            </div>
          </div>

          {/* Performance Dashboard */}
          <div style={{marginBottom:16}}>
            {/* ROI row */}
            <div style={{display:"flex",gap:10,marginBottom:10}}>
              <div style={{flex:1,...tile,borderRadius:12,padding:"14px 16px"}}>
                <div style={{...eyebrow,letterSpacing:"1px",marginBottom:4}}>ROI</div>
                <div style={{fontSize:20,fontWeight:900,color:pnl(roi)}}>{roi>=0?"+":""}{roi.toFixed(1)}%</div>
                <div style={{fontSize:11,color:pnl(net),marginTop:2}}>{fmtMoney(net)} net</div>
              </div>
              <div style={{flex:1,...tile,borderRadius:12,padding:"14px 16px"}}>
                <div style={{...eyebrow,letterSpacing:"1px",marginBottom:4}}>Record</div>
                <div style={{fontSize:20,fontWeight:900,color:JT.text.primary}}>{won.length}–{lost.length}{push.length>0?`–${push.length}`:""}</div>
                <div style={{fontSize:11,color:JT.text.tertiary,marginTop:2}}>{settled.length} slips · {fmtMoney(totalStaked,{sign:false})} staked</div>
              </div>
            </div>

            {/* Best sportsbook */}
            {bestSbInfo&&(
              <div style={{...tile,borderRadius:12,padding:"14px 16px",marginBottom:10,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                <div>
                  <div style={{...eyebrow,letterSpacing:"1px",marginBottom:4}}>Best Sportsbook</div>
                  <div style={{fontSize:15,fontWeight:800,color:JT.text.primary}}>{bestSbInfo.name}</div>
                  <div style={{fontSize:11,color:JT.text.tertiary,marginTop:2}}>{sbMap[bestSb[0]].won}W – {sbMap[bestSb[0]].lost}L{sbMap[bestSb[0]].push>0?` – ${sbMap[bestSb[0]].push}P`:""}</div>
                </div>
                <div style={{textAlign:"right"}}>
                  <div style={{fontSize:18,fontWeight:900,color:pnl(bestSb[1].net)}}>{fmtMoney(bestSb[1].net)}</div>
                  <div style={{fontSize:11,color:JT.text.tertiary,marginTop:2}}>net P&L</div>
                </div>
              </div>
            )}

            {/* P&L by slip type */}
            {propRows.length>0&&(
              <div style={{...tile,borderRadius:12,padding:"14px 16px"}}>
                <div style={{...eyebrow,letterSpacing:"1px",marginBottom:10}}>P&L by Type</div>
                {propRows.map(([key,data])=>{
                  const label = data.label;
                  return(
                    <div key={key} style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
                      <div style={{flex:1}}>
                        <div style={{fontSize:12,fontWeight:700,color:JT.text.secondary}}>{label}</div>
                        <div style={{fontSize:11,color:JT.text.tertiary}}>{data.won}W – {data.lost}L{data.push>0?` – ${data.push}P`:""}</div>
                      </div>
                      <div style={{fontSize:13,fontWeight:800,color:pnl(data.net)}}>{fmtMoney(data.net)}</div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Settled slip cards */}
          {settled.map(bet=>{
            const s = getS(bet);
            const meta = SM[s]||SM.pending;
            // #878: void-aware per-bet payout. A multi with a pushed leg recomputes on
            // survivors where provable; indeterminate → null payout (badge shows "Won"/—),
            // never the over-stated pre-void figure (INV-1). Non-void → unchanged.
            const eff = bet.slipType==="single" ? null : effectiveParlayOdds(bet, games, TRUST_DNP);
            const oddsForPayout = bet.slipType==="single"
              ? bet.odds
              : eff.mode==="recomputed" ? eff.odds
              : (eff.mode==="indeterminate"||eff.mode==="push") ? null
              : eff.odds; // #878 FC-2: combined → combinedOdds (null → suppress, no +100)
            const payout = oddsForPayout==null ? null : calcPayout(bet.stake,oddsForPayout);
            const badge = resultBadge(s, bet.stake, payout);
            const sb = sportsbookForDisplay(bet.sportsbook); // #1199: unknown book -> neutral, never FanDuel
            const isOpen = expanded[bet.id];
            const isMulti = bet.slipType==="sgp"||bet.slipType==="parlay";
            const settledDate = bet.settledAt ? new Date(bet.settledAt).toLocaleDateString("en-US",{month:"short",day:"numeric",hour:"2-digit",minute:"2-digit"}) : "";

            // Signal Scarcity (PR 3, D4): a settled card carries its status in the pill and the
            // signed amount only. The wash, stripe, glow and border tint that repeated the pill
            // are gone; the per-leg rows below keep their outcome tints (live-tracking magnitude).

            return (
              <div key={bet.id} data-testid={`result-card-${bet.id}`} style={{background:JT.bg.card,border:`1px solid ${JT.border.default}`,borderRadius:14,marginBottom:10,overflow:"hidden",transition:"all .3s ease"}}>
                {/* Card header — always visible, tap to expand */}
                <div onClick={()=>setExpanded(e=>({...e,[bet.id]:!e[bet.id]}))}
                  style={{padding:"14px 16px",cursor:"pointer",display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
                  <div style={{flex:1}}>
                    <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:5}}>
                      <span style={{fontSize:11,fontWeight:800,color:meta.color,background:meta.dim,borderRadius:4,padding:"2px 6px",letterSpacing:"0.5px"}}>{meta.icon} {meta.label}</span>
                      <span style={{fontSize:11,color:((Object.prototype.hasOwnProperty.call(JT.book, bet.sportsbook) && JT.book[bet.sportsbook])||sb.color||JT.text.secondary),fontWeight:700}}>{sb.name}</span>
                      <span style={{fontSize:11,color:JT.text.tertiary}}>{bet.slipType==="sgp"?"⚡ SGP":bet.slipType==="parlay"?`🔗 ${bet.legs?.length}-LEG`:getMarketShort(singleMarketSource(bet))||bet.type}</span>
                    </div>
                    {/* Preview — first leg or pick */}
                    {bet.slipType==="single"&&(
                      <div style={{fontSize:13,fontWeight:700,color:JT.text.secondary}}>{teamLegLineText(hydrateSingleLeg(bet,null))||bet.pick}</div>
                    )}
                    {isMulti&&(
                      <div style={{fontSize:12,color:JT.text.tertiary}}>
                        {bet.legs?.slice(0,2).map((l,i)=>{
                          const isP=readsPlayerStat(l, gradingLeague(bet, l, null)); // #1683: nhl:goals names its player
                          const isBin=isBinaryPlayerProp(l);
                          return <span key={i}>{i>0?" · ":""}{isP&&!isBin?`${l.player||l.pick} ${l.overUnder||""} ${l.line||""}`:(teamLegLineText(l)||l.pick)}</span>;
                        })}
                        {bet.legs?.length>2&&<span style={{color:JT.text.tertiary}}> +{bet.legs.length-2} more</span>}
                      </div>
                    )}
                    <div style={{fontSize:11,color:JT.text.tertiary,marginTop:4}}>{settledDate}</div>
                  </div>
                  <div style={{textAlign:"right",minWidth:70}}>
                    <div style={{fontSize:15,fontWeight:900,color:badge.color}}>{badge.display}</div>
                    <div style={{fontSize:11,color:JT.text.tertiary,marginTop:2}}>{isOpen?"▲ less":"▼ details"}</div>
                  </div>
                  {/* #1214 PR B (spec §16.2, P1-4): settled bets are removable too. Same ⋯ rules as
                      SlipTicket: only with onRequestRemove, 44pt via negative margins, and the tap
                      must not reach the row's expand toggle. */}
                  {onRequestRemove&&(
                    <button type="button" aria-label="Bet options" onClick={(e)=>{e.stopPropagation();onRequestRemove(bet);}}
                      style={{minWidth:44,minHeight:44,margin:"-14px -14px -14px 0",padding:0,display:"flex",alignItems:"center",justifyContent:"center",background:"none",border:"none",color:JT.text.tertiary,fontSize:18,lineHeight:1,cursor:"pointer",fontFamily:"inherit"}}>
                      <span aria-hidden="true">⋯</span>
                    </button>
                  )}
                </div>

                {/* Expanded leg breakdown */}
                {isOpen&&(
                  <div style={{padding:"0 16px 14px",borderTop:`1px solid ${JT.border.default}`}}>
                    <div style={{paddingTop:12}}>
                      {/* Odds + stake row */}
                      <div style={{display:"flex",justifyContent:"space-between",marginBottom:10,padding:"8px 10px",background:JT.bg.well,borderRadius:8}}>
                        {[
                          {l:"Stake",v:fmtMoney(bet.stake,{sign:false})},
                          // #878: void-aware odds label — recomputed shows reduced odds, indeterminate shows "—"
                          {l:"Odds",v:isMulti?(eff.mode==="recomputed"?(String(eff.odds).match(/^[+-]/)?eff.odds:`+${eff.odds}`):(eff.mode==="indeterminate"||eff.mode==="push")?"—":(bet.combinedOdds||"—")):fmtOdds(bet.odds)},
                          {l:badge.label,v:badge.value,c:badge.color}
                        ].map(r=>(
                          <div key={r.l} style={{textAlign:"center"}}>
                            <div style={{...eyebrow,letterSpacing:"1px",marginBottom:3}}>{r.l}</div>
                            <div style={{fontSize:13,fontWeight:800,color:r.c||JT.text.secondary}}>{r.v}</div>
                          </div>
                        ))}
                      </div>

                      {/* Leg breakdown for SGP/Parlay */}
                      {isMulti&&bet.legs?.map((leg,i)=>{
                        const isPlayer = readsPlayerStat(leg, gradingLeague(bet, leg, null)); // #1683
                        const isBinary = isBinaryPlayerProp(leg);
                        const frozen = leg.frozenStat;
                        const hasFrozen = frozen !== undefined && frozen !== null;
                        // Determine individual leg outcome
                        // DB-authoritative leg_status takes priority when terminal
                        const legDnp = !leg.status && isDisplayDnpLeg(leg, gradingLeague(bet, leg, null));
                        let legResult = null;
                        if (leg.status && SM[leg.status]) {
                          legResult = leg.status;
                        } else if (legDnp) {
                          legResult = "push"; // #1353 PR 3b (D1): void at the book; the secondary pass writes push
                        } else if (hasFrozen) {
                          if (isBinary) legResult = isNoBinaryLeg(leg) ? (frozen>0?"lost":"won") : (frozen>0?"won":"lost"); // #1744: a No leg is the mirror
                          // #773: never grade a line-required leg off Number(null)=0.
                          // Missing line → leave legResult null (neutral); DB leg_status
                          // is authority. Mirrors the SlipTicket display guard.
                          // #1135: a negative line likewise stays neutral (settles unresolvable).
                          else if (hasLineValue(leg.line) && !isNegativeLine(leg.line)) {
                            const line = Number(leg.line);
                            // #807: shared guard — non-string overUnder must not crash the render.
                            const ou = safeOverUnder(leg.overUnder);
                            const over = ou==="Over"||ou.endsWith("+");
                            legResult = (over?frozen>=line:frozen<=line)?"won":"lost";
                          }
                        }
                        const legMeta = legResult ? SM[legResult] : null;
                        const legColor = legMeta ? legMeta.color : JT.text.tertiary;
                        const legIcon = legMeta ? legMeta.icon : "·";
                        return (
                          <div key={i} data-testid={`leg-row-${i}`} style={{display:"flex",alignItems:"center",gap:8,padding:"7px 10px",background:JT.bg.well,borderRadius:8,border:`1px solid ${legResult?legColor+"22":JT.border.default}`,marginBottom:5}}>
                            <span style={{fontSize:11,fontWeight:900,color:legColor,width:14}}>{legIcon}</span>
                            <div style={{flex:1}}>
                              <div style={{fontSize:11,fontWeight:700,color:JT.text.secondary}}>
                                {isUntrackedLeg(leg)
                                  ? (leg.rawSelectionText||leg.pick||leg.player||"—")
                                  : isPlayer&&!isBinary?formatPropLabel(leg.player||"—",leg.overUnder,leg.line):(teamLegLineText(leg)||leg.pick||"—")}
                              </div>
                              <div style={{fontSize:11,color:JT.text.tertiary}}>{isUntrackedLeg(leg)
                                ? [leg.rawMarketText, "Not tracked yet"].filter(Boolean).join(" · ")
                                : [getMarketLabel(leg)||leg.type||leg.market, leg.frozenDnp === true && legResult === "push" ? "DNP" : null].filter(Boolean).join(" · ")}</div>
                            </div>
                            {isUntrackedLeg(leg)?null:hasFrozen?(
                              <div style={{textAlign:"right"}}>
                                <div style={{fontSize:13,fontWeight:800,color:legColor}}>
                                  {isBinary?(frozen>0?"✓ Scored":"✗ No goal"):!(typeof frozen==="number"&&Number.isFinite(frozen))?"—":(leg.line&&!isNegativeLine(leg.line))?`${frozen}/${leg.line}`:`${frozen}`}
                                </div>
                                <div style={{fontSize:11,color:JT.text.tertiary}}>FINAL</div>
                              </div>
                            ):legMeta?(
                              <div style={{textAlign:"right"}}>
                                <div style={{fontSize:11,fontWeight:700,color:legColor}}>{legMeta.label}</div>
                              </div>
                            ):(
                              <div style={{textAlign:"right"}}>
                                <div style={{fontSize:11,color:JT.text.tertiary,fontStyle:"italic"}}>No stat</div>
                                <div style={{fontSize:11,color:JT.text.tertiary}}>pre-v0.7</div>
                              </div>
                            )}
                          </div>
                        );
                      })}

                      {/* Single bet detail */}
                      {bet.slipType==="single"&&(
                        <div data-testid="single-detail" style={{padding:"8px 10px",background:JT.bg.well,borderRadius:8,border:`1px solid ${JT.border.default}`}}>
                          {isUntrackedLeg(bet.legs?.[0]) ? (<>
                            {/* never-reject PR 3: an untracked single shows the book's own wording */}
                            <div style={{fontSize:12,color:JT.text.secondary,fontWeight:700}}>{bet.legs[0].rawSelectionText||bet.pick||"—"}</div>
                            <div style={{fontSize:11,color:JT.text.tertiary,marginTop:2}}>{[bet.legs[0].rawMarketText,"Not tracked yet"].filter(Boolean).join(" · ")}</div>
                          </>) : (<>
                            <div style={{fontSize:12,color:JT.text.secondary,fontWeight:700}}>{bet.pick}{bet.line?` · ${bet.overUnder||""} ${bet.line}`:""}</div>
                            <div style={{fontSize:11,color:JT.text.tertiary,marginTop:2}}>{getMarketLabel(singleMarketSource(bet))||bet.type} · {bet.teams||"—"}</div>
                          </>)}
                        </div>
                      )}

                      {/* AI Insight */}
                      {(()=>{
                        const ins = insights[bet.id];
                        if(!ins) return (
                          <button onClick={()=>getInsight(bet)}
                            style={{width:"100%",marginTop:10,padding:"10px",background:"linear-gradient(135deg,#0d1f3c,#1e1b4b)",border:"1px solid #4f46e533",borderRadius:10,color:"#a5b4fc",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"inherit",display:"flex",alignItems:"center",justifyContent:"center",gap:6}}>
                            <span>💡</span> Get AI Insight
                          </button>
                        );
                        if(ins.status==="loading") return (
                          <div style={{marginTop:10,padding:"14px",background:JT.bg.well,borderRadius:10,border:`1px solid ${JT.border.mid}`,textAlign:"center"}}>
                            <div style={{width:20,height:20,border:`2px solid ${JT.border.mid}`,borderTopColor:JT.brand.cta,borderRadius:"50%",animation:"spin 0.8s linear infinite",margin:"0 auto 8px"}}/>
                            <div style={{fontSize:11,color:JT.text.tertiary}}>Analyzing your bet history…</div>
                          </div>
                        );
                        if(ins.status==="error") return (
                          <div style={{marginTop:10,padding:"10px",background:"#1c0101",borderRadius:10,border:"1px solid #f8717133",textAlign:"center"}}>
                            <div style={{fontSize:11,color:JT.status.lost.color}}>Could not generate insight. Try again.</div>
                            <button onClick={()=>getInsight(bet)} style={{marginTop:6,background:"none",border:"none",color:"#a5b4fc",fontSize:11,cursor:"pointer",fontFamily:"inherit"}}>↻ Retry</button>
                          </div>
                        );
                        if(ins.status==="done") return (
                          <div style={{marginTop:10,background:"linear-gradient(135deg,#0a0f1e,#0d1530)",borderRadius:10,border:"1px solid #4f46e533",overflow:"hidden"}}>
                            <div style={{padding:"8px 12px",borderBottom:`1px solid ${JT.border.default}`,display:"flex",alignItems:"center",gap:6}}>
                              <span style={{fontSize:14}}>💡</span>
                              <span style={{fontSize:11,fontWeight:800,color:"#a5b4fc",letterSpacing:"1px"}}>AI INSIGHT</span>
                            </div>
                            <div style={{padding:"12px",fontSize:12,color:JT.text.secondary,lineHeight:1.7,whiteSpace:"pre-wrap"}}>{ins.text}</div>
                            {/* #1420: every completed insight carries this line. */}
                            <div style={{padding:"8px 12px 10px",borderTop:`1px solid ${JT.border.default}`,fontSize:11,color:JT.text.tertiary,lineHeight:1.5}}>AI-generated. Not betting advice, and no outcome is guaranteed. For entertainment only.</div>
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </>
      )}
      {/* #1420: the Results-tab responsible-gambling line, shown in every state but loading. */}
      {!syncLoading && (
        <a href={HELPLINE_TEL} style={{display:"flex",alignItems:"center",justifyContent:"center",minHeight:44,marginTop:24,fontSize:11,color:JT.text.tertiary,textDecoration:"none",textAlign:"center"}}>
          21+ · Gambling problem? Call {HELPLINE_LABEL}
        </a>
      )}
    </div>
  );
}
